import { getCurrencySymbol } from '../../utils/currency';

/*
 * Service Charge — one outlet-level figure read off snapshot-details (`serviceFee`).
 *
 * Capture gate: the card, Financial Breakdown column and EOD lines exist ONLY for outlets that
 * capture a service charge (`useServiceChargeEnabled`, from the location-details
 * `uiFeatureFlags`). Pass that flag as `enabled`; when it is false these helpers return null,
 * which every consumer treats as "hide entirely" — never a $0.00 or an empty column. For a
 * capturing outlet with nothing collected the figure is 0 and the structure still renders.
 */
export interface ServiceChargeFigure {
    amount: number;
    /** Retained by outlet → inside Net Sales and taxable. Not retained → pass-through. */
    retained: boolean;
}

export function readServiceCharge(snap: any, enabled: boolean): ServiceChargeFigure | null {
    if (!enabled) return null;
    return { amount: Number(snap?.serviceFee ?? 0) || 0, retained: !!snap?.serviceChargeRetained };
}

/** The two `buildWaterfallData` inputs, straight off snapshot-details (null amount → line hidden). */
export function serviceChargeWaterfallInputs(snap: any, enabled: boolean): { serviceCharge: number | null; serviceChargeRetained: boolean } {
    const sc = readServiceCharge(snap, enabled);
    return { serviceCharge: sc ? sc.amount : null, serviceChargeRetained: sc ? sc.retained : false };
}

export const SERVICE_CHARGE_LABEL = 'Service Charge';

export function serviceChargeTooltip(retained: boolean): string {
    return retained
        ? 'Service charge collected for the period. Retained by the outlet: included in Net Sales and taxed. Not a staff tip or gratuity.'
        : 'Service charge collected for the period. Not retained by the outlet: excluded from Net Sales and not taxed, but included in Total Collected. Not a staff tip or gratuity.';
}

/** Mobile card face: "$X,XXX.XX", compacting to "$X.Xk" above $9,999. */
export function fmtServiceChargeMobile(v: number): string {
    const sym = getCurrencySymbol();
    const n = Number(v) || 0;
    if (Math.abs(n) > 9999) return `${sym}${(n / 1000).toFixed(1)}k`;
    return `${sym}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
